import { useEffect, useRef, useState } from 'react';
import { Icon } from './Icon';
import { PRINT_CLOSE_MESSAGE } from '../lib/printclose';

/**
 * معاينة مستندٍ قبل طباعته — داخل النظام.
 *
 * صفحة الطباعة نفسها تُعرض في إطارٍ داخلي، فيرى المستخدم ما سيخرج على
 * الورق قبل أن يطبع — ويطبع من هنا بلا مغادرة الشاشة إلى تبويبٍ خارجي
 * يضيع بين تبويباته.
 *
 * ## الطباعة من الإطار لا من الصفحة
 *
 * `iframe.contentWindow.print()` يطبع محتوى الإطار وحده بأنماط الطباعة
 * الخاصة به. ولو طُبعت الصفحة الحاوية لخرجت القائمة والترويسة معها.
 * والإطار من أصلنا فالوصول إليه مسموح.
 */
export function DocPreview({
  path,
  title,
  onClose,
}: {
  /** مسار صفحة الطباعة داخل التطبيق، مثل `/print/quotation/abc`. */
  path: string;
  title: string;
  onClose: () => void;
}) {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [ready, setReady] = useState(false);

  /*
   * زرّ «إغلاق» داخل صفحة الطباعة يُغلق هذه الطبقة.
   *
   * هو داخل إطارٍ لا يملك إغلاق حاويته، فيرسل رسالة والحاوية تتكفّل. والأصل
   * يُفحص: رسالةٌ من صفحةٍ أخرى لا تُغلق شاشة المستخدم.
   */
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;
      if (e.data === PRINT_CLOSE_MESSAGE) onClose();
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [onClose]);

  /* الهروب يُغلق المعاينة — ما يفعله المستخدم غريزيًّا في أي طبقة. */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const print = () => {
    const win = frameRef.current?.contentWindow;
    if (!win) return;
    win.focus();
    win.print();
  };

  return (
    <div className="doc-preview" role="dialog" aria-label={`معاينة ${title}`}>
      <div className="doc-preview-bar">
        <button className="btn btn-sm btn-icon btn-ghost" onClick={onClose} aria-label="إغلاق">
          <Icon name="x" size={17} />
        </button>
        <strong className="doc-preview-title">{title}</strong>

        <span className="row" style={{ marginInlineStart: 'auto', gap: 'var(--sp-2)' }}>
          <button className="btn btn-sm btn-primary" onClick={print} disabled={!ready}>
            <Icon name="printer" size={15} />
            طباعة
          </button>
        </span>
      </div>

      <div className="doc-preview-body">
        {!ready && <div className="skeleton" style={{ height: '100%' }} />}
        <iframe
          ref={frameRef}
          src={path}
          title={title}
          onLoad={() => setReady(true)}
          style={{ visibility: ready ? 'visible' : 'hidden' }}
        />
      </div>
    </div>
  );
}
